import { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Banner,
  BlockStack,
  Text,
  Spinner,
  Checkbox,
} from '@shopify/polaris';
import { fetchJsonWithAuth } from '../utils/fetchWithAuth';

interface ContactFormSettingsProps {
  shop: string;
}

interface ContactFormSettingsData {
  contact_form_enabled: boolean;
}

export default function ContactFormSettings({ shop }: ContactFormSettingsProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<ContactFormSettingsData>({
    contact_form_enabled: true,
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, [shop]);

  const loadSettings = async () => {
    try {
      const data = await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`);
      setSettings({
        contact_form_enabled: data.contact_form_enabled === 1 || data.contact_form_enabled === true,
      });
    } catch (err) {
      setError('Failed to load contact form settings. Session may have expired - redirecting...');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await fetchJsonWithAuth(`/api/admin/settings?shop=${encodeURIComponent(shop)}`, {
        method: 'PUT',
        body: JSON.stringify({
          contact_form_enabled: settings.contact_form_enabled ? 1 : 0,
        }),
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save contact form settings. Session may have expired - redirecting...');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToDefault = () => {
    if (!confirm('Are you sure you want to revert the Contact Form setting to its default value? This will enable the Contact Form template.')) {
      return;
    }

    setSettings({
      contact_form_enabled: true,
    });

    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  if (loading) {
    return (
      <Card>
        <BlockStack align="center">
          <Spinner size="small" />
        </BlockStack>
      </Card>
    );
  }

  return (
    <BlockStack gap="500">
      {success && (
        <Banner status="success" onDismiss={() => setSuccess(false)}>
          Contact form settings saved successfully. The page will be updated with the new template.
        </Banner>
      )}

      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {/* Save Settings and Revert Buttons at the top */}
      <div style={{ 
        marginBottom: '32px',
        padding: '28px',
        backgroundColor: '#f6f6f7',
        borderRadius: '10px',
        border: '3px solid #008060',
        boxShadow: '0 4px 16px rgba(0, 128, 96, 0.2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px'
      }}>
        <Button 
          primary 
          onClick={handleSave} 
          loading={saving} 
          size="large"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            backgroundColor: '#008060',
            borderColor: '#008060',
            boxShadow: '0 6px 16px rgba(0, 128, 96, 0.4)',
            transform: 'scale(1.03)',
            transition: 'all 0.2s ease',
            letterSpacing: '0.5px'
          }}
        >
          💾 Save Settings
        </Button>
        <Button 
          onClick={handleRevertToDefault} 
          loading={saving}
          size="large"
          tone="critical"
          style={{
            fontSize: '22px',
            fontWeight: '900',
            padding: '22px 48px',
            minWidth: '280px',
            boxShadow: '0 4px 12px rgba(220, 38, 38, 0.3)',
            letterSpacing: '0.5px'
          }}
        >
          🔄 Revert to Default
        </Button>
      </div>

      <Card>
        <BlockStack gap="400">
          <Text variant="headingMd" as="h2">
            Contact Form Template Settings
          </Text>

          <Checkbox
            label="Enable Contact Form Template"
            checked={settings.contact_form_enabled}
            onChange={(checked) => setSettings({ ...settings, contact_form_enabled: checked })}
            helpText="When enabled, the page uses Shopify's default 'Contact' template, which includes a contact form that allows customers to send emails directly to you. When disabled, it uses the 'Default page' template without a contact form."
          />

          <Banner status="info">
            <BlockStack gap="200">
              <Text as="p" fontWeight="semibold">Template Options:</Text>
              <Text as="p">
                • <strong>Enabled (Contact template):</strong> The page will use Shopify's default Contact template, which includes a contact form that allows customers to send emails directly to you.
              </Text>
              <Text as="p">
                • <strong>Disabled (Default page template):</strong> The page will use the standard Default page template without a contact form.
              </Text>
              <Text as="p" color="subdued">
                After saving, click "Refresh Page" above to apply the template change to your customer page.
              </Text>
            </BlockStack>
          </Banner>
        </BlockStack>
      </Card>
    </BlockStack>
  );
}
